// Session-Ablauf-Warnung.
//
// 04.02.2026 (User-Wunsch): 5 Minuten bevor der Access-Token abläuft
// erscheint ein dezenter Toast „Sitzung läuft ab — jetzt aktualisieren".
// Klick auf „Verlängern" ruft /api/auth/refresh und der User arbeitet
// nahtlos weiter. Falls der User nichts tut, greift der stille Refresh
// im `authFetch` beim nächsten 401 als Fallback.
//
// Kein State im Backend — wir dekodieren den JWT-Payload lokal und
// zählen bis `exp - 5min` runter. Der Token liegt schon im localStorage
// (unter `ees_token`), zusätzlich als httpOnly-Cookie fürs Refresh.

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/auth-fetch';

const WARN_BEFORE_MS = 5 * 60 * 1000;  // 5 Minuten vor exp
const CHECK_INTERVAL_MS = 30 * 1000;   // alle 30 s prüfen
const TOAST_ID = 'session-expiry-warning';

function decodeJwtExp(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    // Base64URL → Base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    const payload = JSON.parse(json);
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
}

export default function SessionExpiryToast() {
  const { user, checkAuth } = useAuth();
  const shownRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Ohne User (nicht eingeloggt) oder auf Landing/öffentlichen Seiten
    // brauchen wir keinen Timer.
    if (!user) {
      shownRef.current = false;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      toast.dismiss(TOAST_ID);
      return;
    }

    const check = () => {
      const token = (() => {
        try { return localStorage.getItem('ees_token'); } catch (_) { return null; }
      })();
      const expMs = decodeJwtExp(token);
      if (!expMs) return;
      const remaining = expMs - Date.now();
      if (remaining <= 0) {
        // Token bereits abgelaufen — Silent-Refresh im authFetch übernimmt.
        // Toast entfernen falls noch offen.
        toast.dismiss(TOAST_ID);
        shownRef.current = false;
        return;
      }
      if (remaining <= WARN_BEFORE_MS && !shownRef.current) {
        shownRef.current = true;
        const mins = Math.max(1, Math.ceil(remaining / 60000));
        toast(
          `Sitzung läuft in ${mins} Minute${mins === 1 ? '' : 'n'} ab`,
          {
            id: TOAST_ID,
            description: 'Jetzt verlängern, damit du ohne Login weiterarbeiten kannst.',
            duration: Infinity,
            action: {
              label: 'Verlängern',
              onClick: async () => {
                try {
                  const r = await authFetch('/api/auth/refresh', { method: 'POST' });
                  if (r.ok) {
                    const data = await r.json().catch(() => ({}));
                    if (data?.token) {
                      try { localStorage.setItem('ees_token', data.token); } catch (_) {}
                    }
                    toast.dismiss(TOAST_ID);
                    shownRef.current = false;
                    toast.success('Sitzung um 12 Stunden verlängert.', { duration: 3000 });
                    // User-Objekt neu laden falls Berechtigungen sich geändert haben.
                    try { await checkAuth?.(); } catch (_) {}
                  } else {
                    toast.error('Verlängern fehlgeschlagen — bitte neu einloggen.');
                  }
                } catch (_) {
                  toast.error('Netzwerkfehler beim Verlängern.');
                }
              },
            },
          },
        );
      } else if (remaining > WARN_BEFORE_MS && shownRef.current) {
        // Refresh war erfolgreich (frischer Token) → Warnung entfernen.
        toast.dismiss(TOAST_ID);
        shownRef.current = false;
      }
    };

    check();  // sofort einmal prüfen
    timerRef.current = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [user, checkAuth]);

  return null;
}
